import {
  WSCard,
  WSElement,
  WSList,
  WSTable,
  WSTableCell,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { IInsuranceCoverage } from "../../../../../services/api/insurance/coverage/types";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../../../../MyResources/selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../../../../MyResources/selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import React, { useState } from "react";
import { useGetNow } from "../../../../../shared/hooks/useGetNow";
import { useModalValidationDetails } from "./ModalValidationDetails";
import { useModalPartialMatchSubmitForReview } from "./ModalPartialMatchSubmitForReview";
import { InsuranceCoverageMonitor } from "../../../../../services/api/insurance/monitor/types";
import { RequirementTableCallback } from "../../../types";
import { ALL_REQUIREMENT_EVENTS } from "../../../utils";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";

type Props = {
  requirement: IRequirementResponse;
  coverages: IInsuranceCoverage[];
  coverageMonitor: InsuranceCoverageMonitor;
  callback: RequirementTableCallback;
};

export const CardPartialMatchSubmitForReview: React.FC<Props> = props => {
  const now = useGetNow();
  const modalValidationDetails = useModalValidationDetails();
  const modalSubmitForReview = useModalPartialMatchSubmitForReview();

  const tableData: WSTableItem<IInsuranceCoverage>[] = props.coverages.map(
    coverage => ({
      id: coverage.insuranceCoverageId,
      data: coverage
    })
  );

  const [selection, setSelection] = useState<WSTableItem<IInsuranceCoverage>[]>(
    props.coverages.length === 1 ? [tableData[0]] : []
  );

  return (
    <WSCard
      header={{
        label: {
          avatar: {
            type: "icon",
            icon: "send-mail",
            colorBackground: "amber300"
          },
          text: "Submit for review"
        }
      }}
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Submit for review",
            disabled: selection.length === 0,
            onClick: () => {
              modalSubmitForReview.open({
                onSuccess: () => {
                  props.callback(
                    ALL_REQUIREMENT_EVENTS.SubmitCoverage,
                    props.requirement
                  );
                },
                insuranceCoverageMonitorId:
                  props.coverageMonitor.insuranceCoverageMonitorId,
                insuranceCoverageId: selection[0].data.insuranceCoverageId
              });
            }
          }
        ]
      }}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          Select a partially matched policy and submit it for client review. If
          the policy does not provide the right coverage or is rejected by the
          client, you will need to seek new coverage and upload the relevant
          documents.
        </WSText.ParagraphSm>

        <WSTable<IInsuranceCoverage>
          tableData={tableData}
          selectionType="radio"
          selection={selection}
          onSelectionChange={setSelection}
          columns={[
            {
              config: {
                header: {
                  text: "Policy"
                }
              },
              renderFunction: row => (
                <WSTableCell text={row.data.title || row.data.policyNumber} />
              )
            },
            {
              config: {
                header: {
                  text: "Status"
                }
              },
              renderFunction: row => {
                return (
                  <WSTableCell
                    pill={selectorInsuranceCoverageStatusPillByExpirationDate(
                      row.data.expirationDate
                    )}
                    secondaryText={selectorInsuranceCoverageStatusDescriptionByInsurance(
                      row.data,
                      now
                    )}
                  />
                );
              }
            },
            {
              config: {
                header: {
                  text: "Result"
                }
              },
              renderFunction: row => (
                <WSElement>
                  <WSTableCell
                    pill={{
                      theme: "warning",
                      icon: true,
                      text: "Partial match"
                    }}
                  />
                  <WSText.ParagraphXs
                    mt="XS"
                    color="blue400"
                    onClick={() => {
                      modalValidationDetails.open({
                        item: row.data,
                        coverageMonitor: props.coverageMonitor
                      });
                    }}
                  >
                    View results
                  </WSText.ParagraphXs>
                </WSElement>
              )
            }
          ]}
        />
      </WSList>
    </WSCard>
  );
};
